import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useQuery } from '@tanstack/react-query';

export interface PolicyDomainAccessResponse {
  canEditAllDomains: boolean;
  organisationDomainIds: number[];
  /** Report domains with an active Content Policy licence; the crawler only scans these. */
  licensedDomainIds: number[];
  /** Report domains whose Content Policy licence has lapsed, as opposed to never issued. */
  expiredDomainIds: number[];
}

/** Whether the current user may create or save an all-domains policy. */
export function useFetchPolicyDomainAccess() {
  return useQuery({
    queryKey: [api.policyDomainAccess],
    queryFn: () =>
      fetchWrapper.get<PolicyDomainAccessResponse>(api.policyDomainAccess),
  });
}

/**
 * Whether the current user is explicitly allowed to edit all-domain policies.
 * While loading or after an error, access is denied until the server says yes.
 */
export function useCanEditAllGovernanceDomains() {
  const { data, isPending } = useFetchPolicyDomainAccess();

  return {
    canEditAllDomains: data?.canEditAllDomains === true,
    isPending,
  };
}

const NO_DOMAINS: number[] = [];

/** Licensed domain ids; empty until the server has answered. */
export function useLicensedGovernanceDomainIds() {
  const { data, isPending, isError } = useFetchPolicyDomainAccess();

  return {
    organisationDomainIds: data?.organisationDomainIds ?? NO_DOMAINS,
    isLicenseUnavailable:
      isPending ||
      isError ||
      !Array.isArray(data?.organisationDomainIds) ||
      !Array.isArray(data?.licensedDomainIds),
    licensedDomainIds: data?.licensedDomainIds ?? NO_DOMAINS,
    expiredDomainIds: data?.expiredDomainIds ?? NO_DOMAINS,
    isPending,
  };
}
